'use client';

import React from 'react';
import Image from 'next/image';
import { cloudinaryService } from '@/services/cloudinaryService';

interface CloudinaryImageProps {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  className?: string;
  fill?: boolean;
  priority?: boolean;
  quality?: 'auto' | 'auto:best' | 'auto:good' | 'auto:eco' | 'auto:low' | number;
  format?: 'auto' | 'webp' | 'jpg' | 'png' | 'avif';
  crop?: 'fill' | 'fit' | 'scale' | 'limit' | 'pad';
  aspectRatio?: string;
  sizes?: string;
  responsive?: boolean;
  responsiveWidths?: number[];
  preset?: 'thumbnail' | 'card' | 'featured' | 'none';
  dpr?: 'auto' | number;
  onClick?: () => void;
}

/**
 * CloudinaryImage Component
 * 
 * A Next.js Image wrapper that automatically applies Cloudinary optimizations:
 * - Automatic format conversion (WebP, AVIF)
 * - Responsive sizing with srcset
 * - Quality optimization
 * - DPR (Device Pixel Ratio) handling
 * - Lazy loading by default
 * 
 * @example
 * // Basic usage with automatic optimization
 * <CloudinaryImage 
 *   src="https://res.cloudinary.com/..."
 *   alt="Product image"
 *   width={600}
 *   height={400}
 * />
 * 
 * @example
 * // Responsive image with custom widths
 * <CloudinaryImage 
 *   src="https://res.cloudinary.com/..."
 *   alt="Hero image"
 *   responsive
 *   responsiveWidths={[320, 640, 1024, 1536]}
 *   sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
 * />
 * 
 * @example
 * // Using presets for common use cases
 * <CloudinaryImage 
 *   src="https://res.cloudinary.com/..."
 *   alt="Thumbnail"
 *   preset="thumbnail"
 * />
 */
const CloudinaryImage: React.FC<CloudinaryImageProps> = ({
  src,
  alt,
  width,
  height,
  className = '',
  fill = false,
  priority = false,
  quality = 'auto:good',
  format = 'auto',
  crop = 'fill',
  aspectRatio,
  sizes,
  responsive = false,
  responsiveWidths = [320, 640, 768, 1024, 1280, 1536],
  preset = 'none',
  dpr = 'auto',
  onClick,
}) => {
  // Handle missing or empty src by providing fallback
  const fallbackImage = '/assets/images/no_image.png';
  const validSrc = src && src.trim() !== '' ? src : fallbackImage;
  
  // Check if URL is a Cloudinary URL
  const isCloudinaryUrl = validSrc?.includes('cloudinary.com') || validSrc?.includes('res.cloudinary.com');

  // Generate optimized URL based on preset or custom options
  const getOptimizedSrc = (): string => {
    if (!isCloudinaryUrl) {
      return validSrc; // Return original if not Cloudinary
    }

    switch (preset) {
      case 'thumbnail':
        return cloudinaryService.getThumbnailUrl(validSrc);
      case 'card':
        return cloudinaryService.getCardImageUrl(validSrc);
      case 'featured':
        return cloudinaryService.getFeaturedImageUrl(validSrc);
      default:
        return cloudinaryService.getOptimizedUrl(validSrc, {
          width,
          height,
          crop,
          quality,
          format,
          dpr,
          aspectRatio,
        });
    }
  };

  // Generate srcset for responsive images
  const getSrcSet = (): string | undefined => {
    if (!responsive || !isCloudinaryUrl) {
      return undefined;
    }

    return cloudinaryService.getResponsiveSrcSet(validSrc, responsiveWidths);
  };

  const optimizedSrc = getOptimizedSrc();
  const srcSet = getSrcSet();

  // Default sizes for responsive images
  const defaultSizes = sizes || '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw';

  // Common props for Image component
  const commonProps = {
    src: optimizedSrc,
    alt,
    className,
    priority,
    onClick,
    // Use Next.js optimization only for non-Cloudinary images
    unoptimized: isCloudinaryUrl,
  };

  if (fill) {
    return (
      <Image
        {...commonProps}
        fill
        sizes={responsive ? defaultSizes : sizes}
        loading={priority ? 'eager' : 'lazy'}
      />
    );
  }

  return (
    <Image
      {...commonProps}
      width={width || 600}
      height={height || 400}
      srcSet={srcSet}
      sizes={responsive ? defaultSizes : sizes}
      loading={priority ? 'eager' : 'lazy'}
    />
  );
};

export default CloudinaryImage;